import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ContactSection } from "@/components/sections/ContactSection";
import { MapPin, Clock, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact FullScale Marketing",
  description: "Book a free strategy call with FullScale Marketing. Australia's specialist digital marketing agency for tradies and construction businesses.",
  url: "https://fullscalemarketing.com.au/contact",
  mainEntity: {
    "@type": "Organization",
    name: "FullScale Marketing",
    email: "info@fullscalemarketing.com.au",
    areaServed: { "@type": "Country", name: "Australia" },
  },
};

const contactDetails = [
  { icon: Mail, label: "Email Us", value: "info@fullscalemarketing.com.au", href: "mailto:info@fullscalemarketing.com.au" },
  { icon: MapPin, label: "Service Area", value: "Australia-wide — all states and regions" },
  { icon: Clock, label: "Response Time", value: "We respond within 24 hours on business days" },
];

export default function Contact() {
  return (
    <>
      <Helmet>
        <title>Contact Us | FullScale Marketing - Book a Free Strategy Call</title>
        <meta
          name="description"
          content="Book a free strategy call with FullScale Marketing. We'll audit your digital presence and create a custom growth plan for your trade business. Australia-wide service."
        />
        <link rel="canonical" href="https://fullscalemarketing.com.au/contact" />
        <meta property="og:title" content="Contact FullScale Marketing | Free Strategy Call" />
        <meta property="og:description" content="Book a free strategy call. We'll audit your digital presence and create a custom growth plan for your trade business." />
        <meta property="og:url" content="https://fullscalemarketing.com.au/contact" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      {/* Quick Contact Info Bar */}
      <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-6 sm:pb-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-background" />
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-8"
          >
            <div className="section-badge mb-4">
              <span className="text-xs">Get In Touch</span>
            </div>
            <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-[3.25rem] font-extrabold text-foreground leading-[1.1] mb-4 tracking-[-0.025em]">
              Let's Talk About <span className="text-primary">Growing Your Business</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-[1.7] max-w-2xl mx-auto mb-2">
              Fill in the form below or book a strategy call. We'll get back to you within 24 hours with a custom plan for your trade business.
            </p>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Not sure what you need? <Link to="/services" className="text-primary font-semibold hover:underline underline-offset-2">Explore our services</Link> or read our <Link to="/case-studies" className="text-primary font-semibold hover:underline underline-offset-2">case studies</Link> to see how we've helped other tradies grow.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {contactDetails.map((detail, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                className="flex items-center gap-3 bg-card rounded-lg p-4 border border-border/50"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <detail.icon className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-foreground text-[0.8125rem] sm:text-sm font-semibold">{detail.label}</p>
                  {detail.href ? (
                    <a href={detail.href} className="text-muted-foreground text-[0.75rem] sm:text-xs hover:text-primary transition-colors break-all">
                      {detail.value}
                    </a>
                  ) : (
                    <p className="text-muted-foreground text-[0.75rem] sm:text-xs">{detail.value}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}
